import Config from "./config";

const PAYOUT_INVOICE_PREFIX = "payout_invoice/";

// Influencer uploads send PDF bytes to an image/jpeg signed URL, so the stored
// key ends with .jpg while the object is a PDF. The extension cannot be trusted;
// the bytes decide how the invoice is rendered.
const IMAGE_SIGNATURES = [
  { type: "image/jpeg", bytes: [0xff, 0xd8, 0xff] },
  { type: "image/png", bytes: [0x89, 0x50, 0x4e, 0x47] },
  { type: "image/gif", bytes: [0x47, 0x49, 0x46, 0x38] },
];

function buildMediaUrl(pathOrId) {
  if (!pathOrId) return "";
  const value = String(pathOrId);
  if (/^(https?:|blob:|data:)/i.test(value)) {
    return value;
  }
  return `${Config.MEDIA_BASE_URL}/${value.replace(/^\/+/, "")}`;
}

export function buildInvoicePreviewCandidates(payout, fallbackPayoutId) {
  const mediaId = String(payout?.invoice_media_id || "").trim();
  const invoicePath = String(payout?.invoice_path || "").trim();
  const invoiceUrl = String(payout?.invoice_url || "").trim();
  const payoutId = payout?.payout_id || fallbackPayoutId;
  const candidates = [];

  if (/^https?:\/\//i.test(invoiceUrl)) {
    candidates.push(invoiceUrl);
  }
  if (invoicePath) {
    candidates.push(buildMediaUrl(invoicePath));
  }
  if (mediaId) {
    if (/^https?:\/\//i.test(mediaId) || mediaId.includes("/")) {
      candidates.push(buildMediaUrl(mediaId));
    } else {
      if (payoutId) {
        candidates.push(
          buildMediaUrl(`${PAYOUT_INVOICE_PREFIX}${payoutId}/${mediaId}`)
        );
      }
      // Signed uploads sometimes land at the bucket root instead of the prefix.
      candidates.push(buildMediaUrl(mediaId));
    }
  }

  return [...new Set(candidates.filter(Boolean))];
}

function startsWithBytes(buffer, signature) {
  if (buffer.byteLength < signature.length) return false;
  const bytes = new Uint8Array(buffer, 0, signature.length);
  return signature.every((byte, index) => bytes[index] === byte);
}

function looksLikePdf(buffer) {
  return startsWithBytes(buffer, [0x25, 0x50, 0x44, 0x46]);
}

function detectImageType(buffer) {
  const match = IMAGE_SIGNATURES.find((signature) =>
    startsWithBytes(buffer, signature.bytes)
  );
  return match ? match.type : "";
}

function looksLikeErrorXml(buffer) {
  if (!buffer.byteLength) return false;
  const header = new TextDecoder().decode(
    buffer.slice(0, Math.min(buffer.byteLength, 120))
  );
  return (
    header.includes("AccessDenied") ||
    header.includes("<Error>") ||
    header.includes("<?xml")
  );
}

function isAmazonS3Url(url) {
  return /amazonaws\.com/i.test(String(url || ""));
}

// Dev only: same-origin route re-serves S3 bytes so fetch is not blocked by CORS.
function buildDevProxyUrl(remoteUrl) {
  return `/invoice-media-proxy?url=${encodeURIComponent(remoteUrl)}`;
}

function buildExternalPdfViewerUrl(remoteUrl) {
  return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
    remoteUrl
  )}`;
}

async function resolveInvoicePreview(remoteUrl) {
  const fetchTargets = [];
  if (
    process.env.NODE_ENV === "development" &&
    isAmazonS3Url(remoteUrl) &&
    /^https?:\/\//i.test(remoteUrl)
  ) {
    fetchTargets.push(buildDevProxyUrl(remoteUrl));
  }
  fetchTargets.push(remoteUrl);

  let lastError = null;
  for (const target of fetchTargets) {
    try {
      const response = await fetch(target);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const buffer = await response.arrayBuffer();
      if (!buffer || !buffer.byteLength) {
        throw new Error("Empty invoice body");
      }
      if (looksLikeErrorXml(buffer)) {
        throw new Error("Access denied");
      }

      const imageType = detectImageType(buffer);
      if (imageType && !looksLikePdf(buffer)) {
        return {
          url: URL.createObjectURL(new Blob([buffer], { type: imageType })),
          kind: "image",
        };
      }

      const contentType = String(response.headers.get("content-type") || "");
      if (
        !looksLikePdf(buffer) &&
        (contentType.includes("xml") || contentType.includes("text/"))
      ) {
        throw new Error("Not a renderable invoice body");
      }

      return {
        url: URL.createObjectURL(
          new Blob([buffer], { type: "application/pdf" })
        ),
        kind: "pdf",
      };
    } catch (error) {
      lastError = error;
    }
  }

  // Production fallback when S3 blocks the fetch: let an external viewer read it.
  if (isAmazonS3Url(remoteUrl)) {
    return { url: buildExternalPdfViewerUrl(remoteUrl), kind: "pdf" };
  }

  throw lastError || new Error("Unable to load invoice preview");
}

export async function resolveFirstInvoicePreview(candidates) {
  let lastError = null;
  for (const candidate of candidates) {
    try {
      return await resolveInvoicePreview(candidate);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error("Unable to load invoice preview");
}

export function revokeInvoicePreviewUrl(url) {
  if (url && String(url).startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}
